import React, { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AtSign, User as UserIcon } from "lucide-react";

/**
 * CommentComposer — inline reply box with @-mention typeahead.
 * Same Flying Buddies-first ranking as the post composer; inserts the
 * `@[Name](user:UUID)` marker so the rendered comment turns it into a
 * clickable link to /crew/:id.
 */
export default function CommentComposer({ postId, onCreated }) {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [userMentions, setUserMentions] = useState([]);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQ, setMentionQ] = useState("");
  const [mentionCaret, setMentionCaret] = useState(null);
  const [mentionResults, setMentionResults] = useState([]);
  const areaRef = useRef(null);
  const mentionTO = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const abs = (u) => (u && u.startsWith("/") ? `${backendUrl}${u}` : u);

  const detectMentionQuery = (val, caret) => {
    const upto = val.slice(0, caret);
    const m = upto.match(/(?:^|\s)@([A-Za-z0-9_ ]{0,40})$/);
    return m ? { q: m[1], at: caret - m[1].length - 1 } : null;
  };

  const onChange = (e) => {
    const val = e.target.value;
    setText(val);
    const caret = e.target.selectionStart || val.length;
    const dm = detectMentionQuery(val, caret);
    if (dm) { setMentionOpen(true); setMentionQ(dm.q); setMentionCaret(dm.at); }
    else { setMentionOpen(false); setMentionQ(""); setMentionCaret(null); }
  };

  useEffect(() => {
    if (!mentionOpen) { setMentionResults([]); return; }
    clearTimeout(mentionTO.current);
    mentionTO.current = setTimeout(async () => {
      try {
        const r = await api.get("/social/users/search", { params: { q: mentionQ, limit: 6 } });
        setMentionResults(r.data.results || []);
      } catch { setMentionResults([]); }
    }, 150);
    return () => clearTimeout(mentionTO.current);
  }, [mentionOpen, mentionQ]);

  const insertUser = (u) => {
    if (mentionCaret === null) return;
    const before = text.slice(0, mentionCaret);
    const after = text.slice(areaRef.current?.selectionStart || (mentionCaret + mentionQ.length + 1));
    const token = `@[${u.name}](user:${u.id}) `;
    const next = before + token + after;
    setText(next);
    setUserMentions((prev) => (prev.find((m) => m.id === u.id) ? prev : [...prev, u]));
    setMentionOpen(false);
    setMentionQ("");
    setMentionCaret(null);
    setTimeout(() => {
      if (areaRef.current) {
        const pos = before.length + token.length;
        areaRef.current.focus();
        areaRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const submit = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      const r = await api.post(`/social/posts/${postId}/comments`, {
        text,
        user_mentions: userMentions.map((u) => u.id),
      });
      onCreated && onCreated(r.data);
      setText("");
      setUserMentions([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Comment failed");
    } finally { setPosting(false); }
  };

  if (!user) return null;

  return (
    <div className="flex gap-2 items-end">
      {user.avatar_url ? (
        <img src={abs(user.avatar_url)} alt="" className="h-8 w-8 rounded-full object-cover border border-white/15 flex-shrink-0" />
      ) : (
        <div className="h-8 w-8 rounded-full bg-sky/30 border border-sky/40 grid place-items-center mono text-[10px] font-bold text-white flex-shrink-0">
          {(user.first_name || "?").charAt(0)}
        </div>
      )}
      <div className="flex-1 relative">
        <div className="flex gap-2 items-end">
          <textarea
            ref={areaRef}
            data-testid={`comment-input-${postId}`}
            value={text}
            onChange={onChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !mentionOpen) { e.preventDefault(); submit(); }
              if (e.key === "Escape") setMentionOpen(false);
            }}
            placeholder="Write a comment… type @ to tag a Flying Buddy"
            rows={1}
            className="flex-1 bg-black/40 border border-white/10 rounded-2xl px-3.5 py-2.5 text-white placeholder:text-white/35 outline-none focus:border-sky/50 resize-none"
            style={{ fontSize: 16 }}
          />
          <button
            onClick={submit}
            disabled={posting || !text.trim()}
            data-testid={`comment-submit-${postId}`}
            className="px-4 py-2.5 rounded-full font-semibold text-sm disabled:opacity-40 min-h-[44px]"
            style={{ background: "#4B9CFF", color: "#0A0F1A" }}
          >
            Send
          </button>
        </div>

        {/* User mention chips */}
        {userMentions.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1" data-testid={`comment-mentions-${postId}`}>
            {userMentions.map((u) => (
              <span key={u.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan/[0.12] border border-cyan/40 text-cyan text-[11px]">
                <UserIcon className="h-2.5 w-2.5" /> {u.name}
              </span>
            ))}
          </div>
        )}

        {/* Typeahead */}
        {mentionOpen && mentionResults.length > 0 && (
          <div className="absolute left-0 right-0 bottom-full mb-1 z-20 rounded-xl border border-cyan/40 bg-obsidian shadow-2xl overflow-hidden" data-testid={`comment-mention-typeahead-${postId}`}>
            <div className="mono text-[10px] tracking-[0.24em] text-cyan/80 uppercase px-3 pt-2.5 pb-1.5 flex items-center gap-1.5">
              <AtSign className="h-3 w-3" /> Tag a Flying Buddy
            </div>
            <div className="max-h-56 overflow-y-auto">
              {mentionResults.map((u) => (
                <button
                  type="button"
                  key={u.id}
                  onMouseDown={(e) => { e.preventDefault(); insertUser(u); }}
                  className="w-full text-left px-3 py-2 hover:bg-cyan/10 transition-colors flex items-center gap-2.5 border-b border-white/[0.04] last:border-0"
                  data-testid={`comment-mention-option-${u.id}`}
                >
                  {u.avatar_url ? (
                    <img src={u.avatar_url} alt="" className="h-7 w-7 rounded-full object-cover" />
                  ) : (
                    <div className="h-7 w-7 rounded-full bg-gradient-to-br from-cyan/50 to-cyan/10 border border-cyan/40 grid place-items-center mono text-[10px] font-bold text-white">
                      {(u.first_name || "?").charAt(0)}{(u.last_name || "").charAt(0)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-white text-sm truncate flex items-center gap-1.5">
                      {u.name}
                      {u.is_buddy && <span className="mono text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded-full bg-avail/15 text-avail border border-avail/40">FLYING BUDDY</span>}
                    </div>
                    <div className="text-[11px] text-white/50 truncate">{u.badge_label || u.role}{u.location_icao ? ` · ${u.location_icao}` : ""}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
